import { PropsWithChildren } from 'react';

import {
  ProviderAvatarBlock,
  ChakraFlex,
  ChakraText,
  DateFormat,
  EquipUIFireflyV2Theme,
  FAIconName,
  Icon,
  IconSize,
  Menu,
  MenuOption,
  ChakraBox,
} from '@equip.health/ui';
import { useHistory } from 'react-router-dom';

import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { UserMode, useModeContext } from '~/lib/contexts/ModeContext';
import { Path } from '~/routes/constants';

import useHandleLogout from '~/lib/hooks/useHandleLogout';
import { useGetProviderAvatarByIdQuery } from '~/store/services/providers';
import { DateTime } from 'luxon';
import { useGetAllOutOfOfficeListQuery } from '~/store/services/profile';
import { getLatestOutOfOfficeEndDate } from '~/lib/util/profile.util';

const {
  neutral: {
    tertiary: neutralTertiary,
    background: { primary: netrualBackgroundPrimary },
  },
} = EquipUIFireflyV2Theme.colors;

const TOP_LEVEL_ID = 'account';

enum AccountMenuLabel {
  LOGOUT = 'Logout',
  SETTINGS = 'Settings',
  VIEW_PROFILE = 'View profile',
}

const MenuItem = ({
  children,
  label,
}: PropsWithChildren<{
  label: AccountMenuLabel;
}>) => (
  <ChakraFlex>
    {children}
    <ChakraText
      id={`${TOP_LEVEL_ID}__menu-item-${label.toLowerCase()}`}
      paddingLeft="12px"
    >
      {label}
    </ChakraText>
  </ChakraFlex>
);

interface AccountMenuProps {
  isSmall?: boolean;
}

const AngleDownIcon = (
  <Icon
    color={neutralTertiary}
    name={FAIconName.chevronDownRegular}
    size={IconSize.medium}
  />
);
const AccountMenu = ({ isSmall = false }: AccountMenuProps) => {
  const history = useHistory();
  const { currentUser } = useGlobalContext();
  const { userId } = currentUser;

  const { mode } = useModeContext();
  const isAdmin = mode === UserMode.ADMIN;
  const isOps = mode === UserMode.OPS;
  const { data: avatarData } = useGetProviderAvatarByIdQuery(
    {
      providerExternalId: currentUser.userId,
    },
    {
      skip: !currentUser?.userId,
    },
  );

  const { data: outOfOfficeListData } = useGetAllOutOfOfficeListQuery({
    providerExternalId: currentUser.userId,
    startDate: DateTime.now().toFormat(DateFormat.shortYearMonthDay),
  });

  const options: MenuOption[] = [
    {
      node: (
        <MenuItem label={AccountMenuLabel.VIEW_PROFILE}>
          <Icon name={FAIconName.circleUserRegular} size="md" />
        </MenuItem>
      ),
      value: AccountMenuLabel.VIEW_PROFILE,
    },

    {
      node: (
        <MenuItem label={AccountMenuLabel.LOGOUT}>
          <Icon name={FAIconName.arrowRightFromBracket} size="md" />
        </MenuItem>
      ),
      value: AccountMenuLabel.LOGOUT,
    },
  ];

  const { logout: handleLogout } = useHandleLogout();

  const MenuSelectMapper: Record<AccountMenuLabel, (value: string) => void> = {
    [AccountMenuLabel.LOGOUT]: () => handleLogout(true),
    [AccountMenuLabel.SETTINGS]: () => history.push(`${Path.SETTINGS}/general`),
    [AccountMenuLabel.VIEW_PROFILE]: () => {
      if (isAdmin) {
        history.push(`${Path.ADMIN_VIEW_PROFILE}/${userId}/info`);
      } else if (isOps) {
        history.push(`${Path.OPS_VIEW_PROFILE}/${userId}/info`);
      } else {
        history.push(`${Path.VIEW_PROFILE}/${userId}/info`);
      }
    },
  };

  const onSelect = (value: string): void => {
    MenuSelectMapper[value]();
  };

  const iconProps = {
    ...(!isSmall && {
      backgroundColor: netrualBackgroundPrimary,
      icon: AngleDownIcon,
    }),
  };
  const legalName = {
    firstName: currentUser.firstName,
    lastName: currentUser.lastName,
    middleName: currentUser?.middleName,
    suffix: currentUser?.suffix,
  };

  const displayName = {
    firstName: currentUser?.displayFirstName,
    lastName: currentUser?.displayLastName,
    middleName: currentUser?.displayMiddleName,
    suffix: currentUser?.displaySuffix,
  };

  const isDisplayName = displayName.firstName && displayName.lastName;

  const credentials = currentUser.credentialsDetails.map(
    (credential) => credential.code,
  );

  const oooEndDate = getLatestOutOfOfficeEndDate(outOfOfficeListData);

  return (
    <ChakraBox
      {...(!isSmall && {
        border: '1px solid',
        borderColor: 'neutral.line',
        borderRadius: '16px',
      })}
    >
      <Menu
        buttonContent={
          <ChakraBox pointerEvents="auto">
            <ProviderAvatarBlock
              avatarOnly={isSmall}
              avatarUrl={avatarData?.url}
              credentials={credentials}
              displayName={isDisplayName && displayName}
              id={`${TOP_LEVEL_ID}__button-content`}
              legalName={legalName}
              oooDate={oooEndDate}
              providerTypeName={currentUser.providerTypeName}
              shouldShowCredentials
              shouldShowFirstNameOnly
              shouldShowOOOIcon={oooEndDate !== null}
              shouldShowProviderType
              variant="standard"
            />
          </ChakraBox>
        }
        buttonHeight="60px"
        buttonSize="md"
        buttonVariant="ghost"
        id={TOP_LEVEL_ID}
        isFullWidth={!isSmall}
        listMaxHeight="300px"
        onSelect={onSelect}
        options={options}
        overflow="hidden"
        padding="12px"
        textAlign="left"
        {...iconProps}
        z-index="1"
      />
    </ChakraBox>
  );
};

export default AccountMenu;
